// "A new version of CAERA is available" — shown to signed-in staff whose tab is
// running an older build than the one now live. See lib/appVersion.js for why.
//
// It asks rather than reloads. A forced reload could land in the middle of a
// half-filled form; the person chooses the moment. "Later" snoozes it for half
// an hour rather than for good, because a tab left on old code for the rest of
// the day is exactly the problem this exists to stop.
import { useEffect, useState } from 'react';
import { onNewVersion } from '../lib/appVersion';

const SNOOZE_MS = 30 * 60 * 1000;

// Held outside the component on purpose. The layout this sits in is rendered
// per route, and routes wrapped in a permission check sit at a different depth
// from routes that are not — so moving between them can remount the banner,
// and a snooze kept in component state would reset and the banner would jump
// straight back. At module level it lasts for the life of the tab.
let snoozedUntil = 0;

export default function UpdateBanner() {
  const [version, setVersion] = useState(null);
  const [, tick] = useState(0);

  useEffect(() => onNewVersion(setVersion), []);

  // Re-render when a snooze runs out, so the banner comes back without needing
  // another deploy to wake it.
  const remaining = snoozedUntil - Date.now();
  useEffect(() => {
    if (remaining <= 0) return undefined;
    const t = setTimeout(() => tick((n) => n + 1), remaining + 50);
    return () => clearTimeout(t);
  }, [remaining > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!version || Date.now() < snoozedUntil) return null;

  return (
    <div role="status" aria-live="polite"
      style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '10px 16px',
        background: 'var(--accent)', color: 'var(--white)', fontSize: 13.5, lineHeight: 1.4 }}>
      <span aria-hidden style={{ fontSize: 16 }}>↻</span>
      <span style={{ flex: 1, minWidth: 220 }}>
        <b>A new version of CAERA is available.</b> Refresh to get the latest fixes — save anything you're in the middle of first.
      </span>
      <span style={{ display: 'flex', gap: 8 }}>
        <button type="button" onClick={() => window.location.reload()}
          style={{ background: 'var(--white)', color: 'var(--accent)', border: 'none', borderRadius: 6, padding: '6px 14px', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
          Refresh
        </button>
        <button type="button" onClick={() => { snoozedUntil = Date.now() + SNOOZE_MS; tick((n) => n + 1); }}
          style={{ background: 'transparent', color: 'var(--white)', border: '1px solid var(--white)', borderRadius: 6, padding: '6px 12px', fontSize: 13, cursor: 'pointer', opacity: 0.85 }}>
          Later
        </button>
      </span>
    </div>
  );
}
